import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  Clock,
  Link as LinkIcon,
  AlertTriangle,
  Search as SearchIcon,
  Lock,
  RefreshCcw,
  Home,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Logo from '../../components/Logo/Logo';
import { getPostLifetimeDays } from '../../utils/postLifecycle';

const STATUS = Object.freeze({
  SUCCESS: 'success',
  EXPIRED: 'expired',
  USED: 'used',
  INVALID: 'invalid',
  NOT_FOUND: 'not-found',
  UNAUTHORIZED: 'unauthorized',
  FAILED: 'failed',
});

const TONE_CLASSES = {
  success: {
    iconBg: 'bg-green-100',
    iconColor: 'text-green-700',
    accent: 'text-green-900',
  },
  warning: {
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-700',
    accent: 'text-amber-900',
  },
  error: {
    iconBg: 'bg-red-100',
    iconColor: 'text-red-700',
    accent: 'text-red-900',
  },
  neutral: {
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-700',
    accent: 'text-gray-900',
  },
};

function getStatusContent(status, { t, lifetimeDays }) {
  switch (status) {
    case STATUS.SUCCESS:
      return {
        tone: 'success',
        Icon: CheckCircle2,
        title: t('extend_confirm_success_title', 'Your post is extended'),
        body:
          lifetimeDays > 0
            ? t(
                'extend_confirm_success_body',
                'Nice work — your post will stay active for another {{count}} days. Other people can keep finding it in the feed.',
                { count: lifetimeDays },
              )
            : t(
                'extend_confirm_success_body_generic',
                'Nice work — your post has been extended and will keep showing up in the feed.',
              ),
        primaryCta: t('extend_confirm_success_cta', 'Open the app'),
        primaryAction: 'home',
      };

    case STATUS.EXPIRED:
      return {
        tone: 'warning',
        Icon: Clock,
        title: t('extend_confirm_expired_title', 'This extension link has expired'),
        body: t(
          'extend_confirm_expired',
          'Extension links only work for a limited time. Open the app and tap "Extend" on the post to keep it active.',
        ),
        primaryCta: t('extend_confirm_open_app_cta', 'Open the app'),
        primaryAction: 'home',
      };

    case STATUS.USED:
      return {
        tone: 'neutral',
        Icon: CheckCircle2,
        title: t('extend_confirm_used_title', 'This link has already been used'),
        body: t(
          'extend_confirm_used',
          "No need to worry — your post was already extended the first time you clicked. You don't need to do anything else.",
        ),
        primaryCta: t('extend_confirm_open_app_cta', 'Open the app'),
        primaryAction: 'home',
      };

    case STATUS.INVALID:
      return {
        tone: 'error',
        Icon: LinkIcon,
        title: t('extend_confirm_invalid_title', "This extension link isn't valid"),
        body: t(
          'extend_confirm_invalid',
          "We couldn't read this extension link. It may have been copied incompletely. Open the app and tap \"Extend\" on the post you want to keep active.",
        ),
        primaryCta: t('extend_confirm_open_app_cta', 'Open the app'),
        primaryAction: 'home',
      };

    case STATUS.NOT_FOUND:
      return {
        tone: 'error',
        Icon: SearchIcon,
        title: t('extend_confirm_not_found_title', "We couldn't find that post"),
        body: t(
          'extend_confirm_not_found',
          "The post you're trying to extend may have been deleted. There's nothing left to extend — but you can post again any time.",
        ),
        primaryCta: t('extend_confirm_open_app_cta', 'Open the app'),
        primaryAction: 'home',
      };

    case STATUS.UNAUTHORIZED:
      return {
        tone: 'error',
        Icon: Lock,
        title: t(
          'extend_confirm_unauthorized_title',
          "This link doesn't match the post",
        ),
        body: t(
          'extend_confirm_unauthorized',
          'Extension links only work for the original post owner. Open the app and sign in with the account that created the post.',
        ),
        primaryCta: t('extend_confirm_open_app_cta', 'Open the app'),
        primaryAction: 'home',
      };

    case STATUS.FAILED:
      return {
        tone: 'error',
        Icon: RefreshCcw,
        title: t('extend_confirm_failed_title', 'Something went wrong'),
        body: t(
          'extend_confirm_failed',
          'We hit a snag extending your post. Please open the app and try again from the My Posts screen.',
        ),
        primaryCta: t('extend_confirm_open_app_cta', 'Open the app'),
        primaryAction: 'home',
      };

    default:
      return {
        tone: 'error',
        Icon: AlertTriangle,
        title: t('extend_confirm_unknown_title', 'Something went wrong'),
        body: t(
          'extend_confirm_unknown',
          "We didn't recognise this confirmation. Please open the app and extend the post from the My Posts screen.",
        ),
        primaryCta: t('extend_confirm_open_app_cta', 'Open the app'),
        primaryAction: 'home',
      };
  }
}

const ExtendConfirm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawStatus = (searchParams.get('status') || '').toLowerCase();
  const postType = searchParams.get('postType') || null;

  const lifetimeDays = useMemo(() => {
    if (!postType || rawStatus !== STATUS.SUCCESS) return 0;
    return getPostLifetimeDays(postType);
  }, [postType, rawStatus]);

  const content = useMemo(
    () => getStatusContent(rawStatus, { t, lifetimeDays }),
    [rawStatus, t, lifetimeDays],
  );

  const tone = TONE_CLASSES[content.tone] || TONE_CLASSES.neutral;
  const { Icon } = content;

  const handlePrimary = () => {
    if (content.primaryAction === 'home') {
      navigate('/');
      return;
    }
    navigate('/');
  };

  return (
    <motion.div
      className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      role="main"
      aria-labelledby="extend-confirm-title"
    >
      <div className="max-w-md w-full text-center space-y-6">
        <motion.div
          className="flex flex-col items-center"
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
        >

          <div className="relative">
            <Logo
              className="w-28 h-28 sm:w-32 sm:h-32 drop-shadow"
              tmClassName="-top-px right-1 text-[0.45rem]"
            />
            <span
              className={[
                'absolute -bottom-1 -right-1 w-10 h-10 sm:w-11 sm:h-11 rounded-full ring-4 ring-background flex items-center justify-center',
                tone.iconBg,
              ].join(' ')}
              aria-hidden="true"
            >
              <Icon className={['w-5 h-5 sm:w-6 sm:h-6', tone.iconColor].join(' ')} />
            </span>
          </div>
        </motion.div>

        <motion.h1
          id="extend-confirm-title"
          className={[
            'text-2xl sm:text-3xl font-bold',
            tone.accent,
          ].join(' ')}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {content.title}
        </motion.h1>

        <motion.p
          className="text-sm sm:text-base text-muted-foreground leading-relaxed"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {content.body}
        </motion.p>

        <motion.div
          className="flex flex-col gap-3 pt-2"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            onClick={handlePrimary}
            className="h-11 px-6 w-full flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" aria-hidden="true" />
            {content.primaryCta}
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ExtendConfirm;